import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Skeleton,
  Alert,
  Card,
  CardContent,
  Checkbox,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import { AuthContext } from '../../context/AuthContext';
import api from '../../services/api';
import { grey, success, fontSize, fontWeight, radius } from '../../styles/tokens';

// Categories d'ingredients pour regroupement
const INGREDIENT_CATEGORIES = {
  'Fruits & Legumes': ['tomate', 'carotte', 'pomme', 'banane', 'orange', 'citron', 'oignon', 'ail', 'poivron', 'courgette', 'aubergine', 'salade', 'laitue', 'epinard', 'brocoli', 'chou', 'haricot vert', 'petit pois', 'avocat', 'concombre', 'celeri', 'poireau', 'champignon', 'fruit', 'legume', 'fraise', 'framboise', 'myrtille', 'raisin', 'poire', 'peche', 'abricot', 'mangue', 'ananas', 'kiwi', 'melon', 'pasteque'],
  'Viandes & Poissons': ['poulet', 'boeuf', 'porc', 'agneau', 'veau', 'dinde', 'canard', 'saumon', 'thon', 'cabillaud', 'colin', 'crevette', 'moule', 'sardine', 'maquereau', 'truite', 'bar', 'dorade', 'viande', 'poisson', 'filet', 'escalope', 'steak', 'jambon', 'lard', 'bacon', 'saucisse'],
  'Produits laitiers': ['lait', 'fromage', 'yaourt', 'yogourt', 'beurre', 'creme', 'ricotta', 'mozzarella', 'parmesan', 'chevre', 'feta', 'emmental', 'gruyere', 'camembert', 'roquefort', 'mascarpone', 'cottage', 'skyr', 'kefir'],
  'Feculents': ['riz', 'pate', 'pain', 'pomme de terre', 'patate', 'quinoa', 'boulgour', 'semoule', 'couscous', 'lentille', 'pois chiche', 'haricot', 'feve', 'avoine', 'flocon', 'cereale', 'muesli', 'granola', 'tortilla', 'wrap', 'bagel'],
  'Epicerie': ['huile', 'vinaigre', 'sel', 'poivre', 'epice', 'herbe', 'sauce', 'moutarde', 'mayonnaise', 'ketchup', 'sucre', 'miel', 'confiture', 'chocolat', 'cacao', 'farine', 'levure', 'oeuf', 'conserve', 'bouillon', 'concentre'],
  'Autres': [],
};

// Fonction pour categoriser un ingredient
const categorizeIngredient = (ingredientName) => {
  const name = (ingredientName || '').toLowerCase();
  for (const [category, keywords] of Object.entries(INGREDIENT_CATEGORIES)) {
    if (category === 'Autres') continue;
    if (keywords.some(keyword => name.includes(keyword))) {
      return category;
    }
  }
  return 'Autres';
};

/**
 * Vue Liste de Courses Client - Version simplifiee mobile-first
 */
const ClientShopping = () => {
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shoppingList, setShoppingList] = useState([]);
  const [checkedItems, setCheckedItems] = useState(() => {
    const saved = localStorage.getItem('clientShoppingChecked');
    return saved ? JSON.parse(saved) : [];
  });
  const [resetDialogOpen, setResetDialogOpen] = useState(false);

  useEffect(() => {
    const fetchCurrentPlan = async () => {
      try {
        const planData = await api.get('/client/weekplan/current');

        if (!planData || !planData.dayPlans) {
          setError('no_plan');
          setLoading(false);
          return;
        }

        // Generer la liste de courses depuis les ingredients
        const allIngredients = [];
        for (const dayPlan of planData.dayPlans || []) {
          for (const mealKey of ['breakfast', 'morningSnack', 'lunch', 'snack', 'dinner']) {
            const meal = dayPlan.meals?.[mealKey];
            if (meal && meal.ingredients) {
              allIngredients.push(...meal.ingredients);
            }
          }
        }

        // Agreger les ingredients par nom
        const aggregated = {};
        for (const ing of allIngredients) {
          const key = (ing.name || ing.ciqual_code || '').toLowerCase().trim();
          if (!key) continue;
          if (aggregated[key]) {
            aggregated[key].quantity += ing.quantity || 0;
          } else {
            aggregated[key] = {
              name: ing.name || key,
              quantity: ing.quantity || 0,
              unit: ing.unit || 'g',
              category: categorizeIngredient(ing.name),
            };
          }
        }

        // Trier par categorie puis par nom
        const sorted = Object.values(aggregated).sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return a.name.localeCompare(b.name);
        });

        setShoppingList(sorted);
        setLoading(false);
      } catch (err) {
        console.error('Erreur lors de la recuperation du plan:', err.message);
        if (err.message?.includes('Aucun plan') || err.response?.status === 404) {
          setError('no_plan');
        } else {
          setError(err.message);
        }
        setLoading(false);
      }
    };

    if (user) {
      fetchCurrentPlan();
    }
  }, [user]);

  const toggleItem = (itemName) => {
    setCheckedItems((prev) => {
      const updated = prev.includes(itemName)
        ? prev.filter((item) => item !== itemName)
        : [...prev, itemName];
      localStorage.setItem('clientShoppingChecked', JSON.stringify(updated));
      return updated;
    });
  };

  const handleResetClick = () => {
    setResetDialogOpen(true);
  };

  const handleResetConfirm = () => {
    setCheckedItems([]);
    localStorage.removeItem('clientShoppingChecked');
    setResetDialogOpen(false);
  };

  const handleResetCancel = () => {
    setResetDialogOpen(false);
  };

  // Formater la quantite
  const formatQuantity = (item) => {
    const qty = Math.round(item.quantity || 0);
    if (qty >= 1000 && item.unit === 'g') {
      return `${(qty / 1000).toFixed(1)} kg`;
    }
    if (qty >= 1000 && item.unit === 'ml') {
      return `${(qty / 1000).toFixed(1)} L`;
    }
    return `${qty} ${item.unit}`;
  };

  // Loading state
  if (loading) {
    return (
      <Box>
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: radius.lg, mb: 2 }} />
        {[...Array(8)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={56} sx={{ borderRadius: radius.md, mb: 1 }} />
        ))}
      </Box>
    );
  }

  // No plan state
  if (error === 'no_plan') {
    return (
      <>
        <Helmet>
          <title>Liste de Courses - Yoco</title>
        </Helmet>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            textAlign: 'center',
            px: 2,
          }}
        >
          <CalendarIcon sx={{ fontSize: 64, color: grey[300], mb: 2 }} />
          <Typography
            sx={{
              fontSize: fontSize['2xl'],
              fontWeight: fontWeight.semibold,
              color: grey[800],
              mb: 1,
            }}
          >
            Aucun programme actif
          </Typography>
          <Typography
            sx={{
              fontSize: fontSize.base,
              color: grey[500],
              maxWidth: 300,
            }}
          >
            Votre coach n'a pas encore publie de programme pour vous.
          </Typography>
        </Box>
      </>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  const toBuyCount = shoppingList.filter((item) => !checkedItems.includes(item.name)).length;
  const checkedCount = checkedItems.filter(name => shoppingList.some(item => item.name === name)).length;

  // Grouper par categorie
  const groupedByCategory = shoppingList.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <>
      <Helmet>
        <title>Liste de Courses - Yoco</title>
      </Helmet>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* Header avec stats */}
        <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}` }}>
          <CardContent sx={{ py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography sx={{ fontSize: fontSize.xs, color: grey[500], textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Cette semaine
                </Typography>
                <Typography sx={{ fontSize: fontSize['2xl'], fontWeight: fontWeight.bold, color: grey[900] }}>
                  {shoppingList.length} ingredients
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                  label={`${checkedCount}/${shoppingList.length}`}
                  size="small"
                  sx={{
                    bgcolor: checkedCount === shoppingList.length && shoppingList.length > 0 ? success[100] : grey[100],
                    color: checkedCount === shoppingList.length && shoppingList.length > 0 ? success.dark : grey[700],
                    fontWeight: fontWeight.medium,
                  }}
                />
                {checkedCount > 0 && (
                  <IconButton size="small" onClick={handleResetClick} sx={{ color: grey[400] }}>
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Liste par categorie */}
        {Object.entries(groupedByCategory).map(([category, items]) => {
          const categoryCheckedCount = items.filter(item => checkedItems.includes(item.name)).length;

          return (
            <Box key={category}>
              {/* Titre categorie */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, px: 0.5 }}>
                <Typography sx={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: grey[700] }}>
                  {category}
                </Typography>
                <Typography sx={{ fontSize: fontSize.xs, color: grey[400] }}>
                  {categoryCheckedCount}/{items.length}
                </Typography>
              </Box>

              {/* Items de la categorie */}
              <Card sx={{ borderRadius: radius.lg, boxShadow: 'none', border: `1px solid ${grey[200]}`, overflow: 'hidden' }}>
                {items.map((item, index) => {
                  const isChecked = checkedItems.includes(item.name);
                  const isLast = index === items.length - 1;

                  return (
                    <Box
                      key={item.name}
                      onClick={() => toggleItem(item.name)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        cursor: 'pointer',
                        bgcolor: isChecked ? grey[50] : '#FFFFFF',
                        borderBottom: isLast ? 'none' : `1px solid ${grey[100]}`,
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: grey[50],
                        },
                      }}
                    >
                      <Checkbox
                        checked={isChecked}
                        size="small"
                        sx={{
                          p: 0,
                          color: grey[300],
                          '&.Mui-checked': {
                            color: success.main,
                          },
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleItem(item.name)}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontSize: fontSize.base,
                            fontWeight: fontWeight.medium,
                            color: isChecked ? grey[400] : grey[900],
                            textDecoration: isChecked ? 'line-through' : 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {item.name}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: fontSize.sm,
                          color: isChecked ? grey[300] : grey[500],
                          textDecoration: isChecked ? 'line-through' : 'none',
                          flexShrink: 0,
                        }}
                      >
                        {formatQuantity(item)}
                      </Typography>
                    </Box>
                  );
                })}
              </Card>
            </Box>
          );
        })}

        {/* Message si tout est achete */}
        {toBuyCount === 0 && shoppingList.length > 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CheckCircleIcon sx={{ fontSize: 48, color: success.main, mb: 1 }} />
            <Typography sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: grey[800] }}>
              Liste complete !
            </Typography>
            <Typography sx={{ fontSize: fontSize.sm, color: grey[500] }}>
              Tous les ingredients ont ete coches.
            </Typography>
          </Box>
        )}
      </Box>

      {/* Modal de confirmation reset */}
      <Dialog
        open={resetDialogOpen}
        onClose={handleResetCancel}
        PaperProps={{
          sx: {
            borderRadius: radius.lg,
            maxWidth: 320,
            mx: 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: fontSize.lg, fontWeight: fontWeight.semibold, pb: 1 }}>
          Reinitialiser la liste ?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: fontSize.sm, color: grey[600] }}>
            Tous les articles coches seront demarques. Cette action ne peut pas etre annulee.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleResetCancel}
            sx={{
              color: grey[600],
              fontWeight: fontWeight.medium,
            }}
          >
            Annuler
          </Button>
          <Button
            onClick={handleResetConfirm}
            variant="contained"
            sx={{
              bgcolor: grey[900],
              color: '#FFFFFF',
              fontWeight: fontWeight.medium,
              '&:hover': {
                bgcolor: grey[800],
              },
            }}
          >
            Reinitialiser
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ClientShopping;
